import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { CompetenciesEditor } from 'components/competencies';
import { CompetencyInProduct } from 'shared-types';

interface Props {
  allowOnlyUserEditableCompetencies?: boolean;
  excludedCompetencies: CompetencyInProduct[];
  targetUserId: number;
  updateCompetencies: () => Promise<void>;
}

const ProfileCompetenciesAdd = ({
  allowOnlyUserEditableCompetencies,
  excludedCompetencies,
  targetUserId,
  updateCompetencies
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, serverConfig, user } = useReduxState();

  const competencyImprovementsEnabled =
    serverConfig.featureFlags.enableCompetencyImprovements;
  const isOwnProfile = targetUserId === user?.id;

  const { data: openRequests, updateData: updateOpenRequests } = useApiData<{
    pendingCompetencyRequests: CompetencyInProduct[];
  }>({
    onlyIf: competencyImprovementsEnabled && isOwnProfile,
    url: `/admin/users/${targetUserId}/competencies`
  });

  const handleSubmit = async (
    competencies: {
      id: number;
      levelId: number | null;
      comment?: string | null;
    }[]
  ) => {
    const response = await api.post(
      `/admin/users/${targetUserId}/competencies`,
      {
        competencies
      }
    );

    if (response.data?.success) {
      dispatch.addNotice({
        text: response.data.queuedCompetencies?.length
          ? messages.competenciesAddRequestSent
          : messages.competenciesAddMultipleSuccess,
        type: 'success'
      });

      await updateCompetencies();
      if (competencyImprovementsEnabled && isOwnProfile) {
        await updateOpenRequests();
      }
    }
  };

  return allowOnlyUserEditableCompetencies && !isOwnProfile ? null : (
    <CompetenciesEditor
      allowOnlyUserEditableCompetencies={allowOnlyUserEditableCompetencies}
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      buttonText={messages.competenciesAdd}
      excludedCompetencies={[
        ...excludedCompetencies,
        ...(openRequests?.pendingCompetencyRequests || [])
      ]}
      grantOnly={
        competencyImprovementsEnabled && !allowOnlyUserEditableCompetencies
      }
      isGranting={true}
      modalActionText={messages.commonAdd}
      modalTitle={messages.competenciesAdd}
      onSubmit={handleSubmit}
      selfAddOnly={competencyImprovementsEnabled && isOwnProfile}
    />
  );
};

export default ProfileCompetenciesAdd;
